import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import suitcss from '../../../helpers/suitcss';
import StageLinkButton from '../../basics/button/StageLinkButton';
import connectUI from '../../basics/ui/UIConnector';
import MediaImage from '../../basics/media/MediaImage';
import matchMediaConnector from '../../../containers/service/ServiceMatchMedia';

const componentName = 'ContentStageSlim';
const ContentStageSlim = (props) => {
  // Define the widths for image variations
  // Should be delivered by the CMS but for now we use a fixed set for desktopImage and mobileImage
  const IMAGE_VARIATION_WIDTHS = ['320w', '640w', '768w', '960w', '1024w', '1280w', '2048w'];
  const { isMediaS } = props;

  const prepareContainerStyling = (stage) => {
    const { backgroundColorValue, backgroundGradient } = stage;
    const setBackground = backgroundGradient
      ? { background: backgroundGradient }
      : { backgroundColor: backgroundColorValue || '#fff' };

    return {
      ...setBackground,
    };
  };

  const renderTextBox = (stage) => {
    const {
      headline,
      headlineMobile,
      subline,
      secondSubline,
      sublineMobile,
    } = stage;

    return (
      <div
        className={suitcss({
          componentName,
          descendantName: 'textBox',
        })}
      >
        <h1
          className={suitcss({
            componentName,
            descendantName: 'headline',
            modifiers: ['desktop'],
          })}
        >
          {headline}
        </h1>
        <h1
          className={suitcss({
            componentName,
            descendantName: 'headline',
            modifiers: ['mobile'],
          })}
        >
          {headlineMobile || headline}
        </h1>

        <p
          className={suitcss({
            componentName,
            descendantName: 'subline',
            modifiers: ['desktop'],
          })}
        >
          <span>{subline}</span>
          {secondSubline && (
            <span>{secondSubline}</span>
          )}
        </p>
        <p
          className={suitcss({
            componentName,
            descendantName: 'subline',
            modifiers: ['mobile'],
          })}
        >
          {sublineMobile || (
            <>
              <span>{subline}</span>
              {secondSubline && (
                <span>{secondSubline}</span>
              )}
            </>
          )}
        </p>
      </div>
    );
  };

  const generateImageVariationsFromSrc = (imageSrc) => {
    if (!imageSrc) return {};
    const variations = {};

    IMAGE_VARIATION_WIDTHS.forEach((width) => {
      variations[width] = imageSrc.replace(/\/media\/cache\/default\//, `/media/cache/${width}/`);
    });

    return variations;
  };

  const renderStageImage = (stage, numberOfStages) => {
    const { desktopImage, mobileImage } = stage;
    // Prepare variations for the MediaImage component
    if (!desktopImage && !mobileImage) {
      return null;
    }

    const sizesAttr = {};

    if (numberOfStages === 2) {
      // two stages -> two columns until 1024px -> use 50% width for each stage
      // 3x 16px = 48px spacing between stages, 24px for each of one, css limits width to 576px
      sizesAttr['(min-width: 1024px)'] = 'min(calc(50vw - 24px), 576px)';
      // smallscreen only 2x 8px = 16px spacing
      sizesAttr[''] = 'calc(100vw - 16px)';
    } else {
      // one stage, use full width
      sizesAttr['(min-width: 1024px)'] = 'min(calc(100vw - 32px), 1216px)';
      sizesAttr[''] = 'calc(100vw - 16px)';
    }

    const finalMobileImage = mobileImage || desktopImage;
    const altAttr = desktopImage.alt || finalMobileImage.alt || '';
    const widthAttr = isMediaS ? finalMobileImage.width : desktopImage.width;
    const heightAttr = isMediaS ? finalMobileImage.height : desktopImage.height;
    const imageVariations = isMediaS
      ? generateImageVariationsFromSrc(finalMobileImage.src)
      : generateImageVariationsFromSrc(desktopImage.src);

    const fetchPriority = props.params.config.behaviorSettings?.fetchPriority ? 'high' : undefined;

    return (
      <span
        className={suitcss({
          componentName,
          descendantName: 'image',
          modifiers: [isMediaS ? 'mobile' : 'desktop'],
        })}
      >
        <MediaImage
          variations={imageVariations}
          sizes={sizesAttr}
          renderWidth={widthAttr}
          renderHeight={heightAttr}
          alt={altAttr}
          isLazy={fetchPriority !== 'high'}
          fetchPriority={fetchPriority}
        />
      </span>
    );
  };

  const handleClick = (url) => {
    const { dispatch } = props;
    dispatch(push(url));
  };

  const renderContentStage = (stage, index, numberOfStages) => (
    <div
      className={suitcss({
        componentName,
        descendantName: 'item',
        modifiers: [stage.toggleAlternativeTextColor ? 'textLight' : 'textDefault'],
      })}
      style={prepareContainerStyling(stage)}
      key={`contentStage-item-${index}`}
      onClick={() => handleClick(stage.button.url)}
    >
      {renderTextBox(stage)}
      <div
        className={suitcss({
          componentName,
          descendantName: 'imageContainer',
        })}
      >
        {renderStageImage(stage, numberOfStages)}
        <StageLinkButton
          label={stage.button.title || 'Test'}
          target={stage.button.url}
          showBorder
          highlightButton={stage.toggleAlternativeButtonStyle}
          ariaLabel={stage.buttonAriaLabel}
        />
      </div>
    </div>
  );
  const { params: { stages, borderless } } = props;
  if (!stages) return null;
  const isSplitContentStage = stages.length > 1;
  return (
    <section
      className={suitcss({
        componentName,
        modifiers: [isSplitContentStage ? 'splitStage' : '', borderless ? 'borderless' : ''],
      })}
    >
      {stages.map((stage, index) => renderContentStage(stage, index, stages.length))}
    </section>
  );
};

ContentStageSlim.propTypes = {
  dispatch: PropTypes.func,
  isMediaS: PropTypes.bool.isRequired,
  params: PropTypes.shape({
    stages: PropTypes.arrayOf(
      PropTypes.shape({
        headline: PropTypes.string.isRequired,
        headlineMobile: PropTypes.string,
        subline: PropTypes.string.isRequired,
        secondSubline: PropTypes.string,
        sublineMobile: PropTypes.string,
        toggleAlternativeTextColor: PropTypes.bool,
        desktopImage: PropTypes.object.isRequired,
        mobileImage: PropTypes.object.isRequired,
        backgroundColorValue: PropTypes.string.isRequired,
        backgroundGradient: PropTypes.string,
        button: PropTypes.object.isRequired,
        buttonAriaLabel: PropTypes.string,
        toggleAlternativeButtonStyle: PropTypes.bool,
      }),
    ),
    borderless: PropTypes.bool,
    config: PropTypes.oneOfType([
      PropTypes.shape({
        behaviorSettings: PropTypes.object,
      }),
      PropTypes.array,
    ]),
  }),
};

ContentStageSlim.defaultProps = {
  params: {
    stages: [],
    borderless: false,
    config: {
      behaviorSettings: {},
    },
  },
};

const mapDispatchToProps = dispatch => ({ dispatch });
export default compose(
  connect(mapDispatchToProps),
  matchMediaConnector(['isMediaS']),
  connectUI(),
)(ContentStageSlim);
